import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip,
} from 'chart.js';
import CanvasHeatmapOverlay from './CanvasHeatmapOverlay';
import { getGoogleMapsLoaderOptions } from './googleMapsLoaderConfig';
import { useNavigate } from 'react-router-dom';
import {
  FiAlertCircle,
  FiBarChart2,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiEdit3,
  FiExternalLink,
  FiFileText,
  FiFilter,
  FiLayers,
  FiList,
  FiMap,
  FiMapPin,
  FiMaximize2,
  FiMessageCircle,
  FiMinimize2,
  FiRefreshCw,
  FiSearch,
  FiTarget,
  FiUser,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import api, { analiseFrequenciaService } from '../../services/api';
import {
  STATUS,
  STATUS_OPTIONS,
  TRI_STATE_OPTIONS,
  calcularResumo,
  construirRegioes,
  coordenadasDoMembro,
  filtrarMembros,
  formatarData,
  formatarTriState,
  gerarInsights,
  getStatusConfig,
  ordenarMembros,
  paraTriState,
  statusDoMembro,
  temCoordenadas,
} from './analiseFrequenciaHelpers';
import {
  ABAS_EXPORTACAO,
  ABAS_EXPORTACAO_IDS,
  abasPadraoSelecionadas,
  listaAbasSelecionadas,
} from './analiseFrequenciaExportOptions';
import { exportarAnaliseFrequenciaExcel } from './exportarAnaliseFrequenciaExcel';
import { exportarAnaliseFrequenciaPdf } from './exportarAnaliseFrequenciaPdf';
import './AnaliseFrequenciaTerritorial.css';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Legend, Tooltip);

const CENTRO_AREIOPOLIS = { lat: -22.6607, lng: -48.6606 };
const MAP_OPTIONS = {
  fullscreenControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  gestureHandling: 'cooperative',
  clickableIcons: false,
};
const PRESENCA_GRADIENT = [
  'rgba(21,128,61,0)',
  'rgba(74,222,128,.35)',
  'rgba(34,197,94,.65)',
  'rgba(21,128,61,.9)',
];
const ATENCAO_GRADIENT = [
  'rgba(245,158,11,0)',
  'rgba(251,191,36,.45)',
  'rgba(249,115,22,.72)',
  'rgba(220,38,38,.95)',
];

const FILTROS_INICIAIS = {
  categorias: ['crianca', 'jovem'],
  status: [],
  bairro: '',
  busca: '',
  tipo_pessoa: '',
  localizacao: 'todos',
  batizado: '',
  visita_indicada: '',
  aceitou_visita: '',
};

const CAMPOS_FREQUENCIA = [
  ['comparece_reuniao', 'Comparece à reunião?'],
  ['frequente_reuniao', 'É frequente?'],
  ['batizado', 'Batizado?'],
  ['visita_indicada', 'Visita indicada?'],
  ['aceitou_visita', 'Aceitou visita?'],
];

function valorSelectTriState(value) {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return 'null';
}

function TriStateSelect({ id, label, value, onChange, compact = false }) {
  return (
    <label className={compact ? 'aft-field aft-field-compact' : 'aft-field'} htmlFor={id}>
      <span>{label}</span>
      <select id={id} value={value} onChange={(event) => onChange(event.target.value)}>
        {TRI_STATE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

function ModalEdicao({ membro, salvando, onClose, onSave }) {
  const [form, setForm] = useState(() => Object.fromEntries(
    CAMPOS_FREQUENCIA.map(([campo]) => [campo, membro[campo] ?? null])
  ));

  useEffect(() => {
    const onKeyDown = (event) => { if (event.key === 'Escape' && !salvando) onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, salvando]);

  return (
    <div className="aft-modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !salvando) onClose();
    }}>
      <section className="aft-modal" role="dialog" aria-modal="true" aria-labelledby="aft-modal-title">
        <div className="aft-modal-header">
          <div>
            <span className="aft-eyebrow">Atualização de frequência</span>
            <h2 id="aft-modal-title">{membro.nome}</h2>
          </div>
          <button type="button" className="aft-icon-button" onClick={onClose} disabled={salvando} aria-label="Fechar">
            <FiX />
          </button>
        </div>
        <p className="aft-modal-help">
          Use “Não informado” quando não houver confirmação. Os campos não são obrigatórios.
        </p>
        <div className="aft-modal-grid">
          {CAMPOS_FREQUENCIA
            .filter(([campo]) => campo !== 'batizado' || membro.categoria === 'jovem')
            .map(([campo, label]) => (
              <label className="aft-field" htmlFor={`editar-${campo}`} key={campo}>
                <span>{label}</span>
                <select
                  id={`editar-${campo}`}
                  value={valorSelectTriState(form[campo])}
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    [campo]: paraTriState(event.target.value),
                  }))}
                >
                  <option value="null">Não informado</option>
                  <option value="true">Sim</option>
                  <option value="false">Não</option>
                </select>
              </label>
            ))}
        </div>
        <div className="aft-modal-actions">
          <button type="button" className="aft-button aft-button-secondary" onClick={onClose} disabled={salvando}>
            Cancelar
          </button>
          <button type="button" className="aft-button aft-button-primary" onClick={() => onSave(form)} disabled={salvando}>
            {salvando ? <><span className="aft-mini-spinner" /> Salvando…</> : <><FiEdit3 /> Salvar atualização</>}
          </button>
        </div>
      </section>
    </div>
  );
}

function Indicador({ label, value, detail, tone = 'neutral', title }) {
  return (
    <article className={`aft-kpi aft-kpi-${tone}`} title={title}>
      <span className="aft-kpi-label">{label}</span>
      <strong>{value}</strong>
      {detail && <span className="aft-kpi-detail">{detail}</span>}
    </article>
  );
}

const AnaliseFrequenciaTerritorial = () => {
  const navigate = useNavigate();
  const { canReadModule, canWriteModule } = useAuth();
  // Somente admins têm acesso por padrão.
  // Para habilitar acesso granular no futuro, basta conceder o módulo
  // 'analise_territorial' { read: true } nas permissões do usuário.
  const podeLer = canReadModule('analise_territorial');
  const podeEditar = canWriteModule('irmandade');
  const googleApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const { isLoaded, loadError } = useJsApiLoader(getGoogleMapsLoaderOptions(googleApiKey));

  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const [dados, setDados] = useState({ meta: null, membros: [] });
  const [bairrosCatalogo, setBairrosCatalogo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [filtrosAbertos, setFiltrosAbertos] = useState(true);
  const [indicadoresAbertos, setIndicadoresAbertos] = useState(true);
  const [modoMapa, setModoMapa] = useState('pontos');
  const [mapInstance, setMapInstance] = useState(null);
  const [membroSelecionado, setMembroSelecionado] = useState(null);
  const [painelAberto, setPainelAberto] = useState(true);
  const [abaPainel, setAbaPainel] = useState('pessoas');
  const [ordenacao, setOrdenacao] = useState('status');
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [geocodificando, setGeocodificando] = useState(false);
  const [telaCheia, setTelaCheia] = useState(false);
  const mapCardRef = useRef(null);
  const [comparativo, setComparativo] = useState({
    presenca: true,
    atencao: true,
    opacidadePresenca: 0.65,
    opacidadeAtencao: 0.7,
  });
  const exportRef = useRef(null);
  const requisicaoRef = useRef(0);
  const [exportAberto, setExportAberto] = useState(false);
  const [exportFormato, setExportFormato] = useState('excel');
  const [exportAbas, setExportAbas] = useState(abasPadraoSelecionadas);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    if (!podeLer) navigate('/dashboard', { replace: true });
  }, [navigate, podeLer]);

  useEffect(() => {
    const timer = setTimeout(() => setBuscaDebounced(filtros.busca.trim()), 350);
    return () => clearTimeout(timer);
  }, [filtros.busca]);

  useEffect(() => {
    const fechar = (event) => {
      if (exportRef.current && !exportRef.current.contains(event.target)) setExportAberto(false);
    };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, []);

  const filtrosRequisicao = useMemo(() => ({
    categorias: filtros.categorias,
    status: filtros.status,
    bairro: filtros.bairro,
    busca: buscaDebounced,
    tipo_pessoa: filtros.tipo_pessoa,
    localizacao: filtros.localizacao,
    batizado: filtros.batizado,
    visita_indicada: filtros.visita_indicada,
    aceitou_visita: filtros.aceitou_visita,
  }), [
    buscaDebounced,
    filtros.aceitou_visita,
    filtros.bairro,
    filtros.batizado,
    filtros.categorias,
    filtros.localizacao,
    filtros.status,
    filtros.tipo_pessoa,
    filtros.visita_indicada,
  ]);

  const carregarDados = useCallback(async () => {
    if (!podeLer) return;
    const requisicaoAtual = ++requisicaoRef.current;
    setLoading(true);
    setErro('');
    try {
      const resposta = await analiseFrequenciaService.buscar(filtrosRequisicao);
      if (requisicaoAtual !== requisicaoRef.current) return;
      const membros = resposta.membros || [];
      setDados({ ...resposta, membros });
      if (!filtrosRequisicao.bairro) {
        setBairrosCatalogo((atuais) => Array.from(new Set([
          ...atuais,
          ...(resposta.regioes || []).map((regiao) => regiao.bairro),
        ])).sort((a, b) => a.localeCompare(b, 'pt-BR')));
      }
      setMembroSelecionado((atual) => (
        atual ? membros.find((membro) => membro.id === atual.id) || null : null
      ));
    } catch (error) {
      if (requisicaoAtual !== requisicaoRef.current) return;
      setErro(error.message || 'Não foi possível carregar a análise territorial.');
    } finally {
      if (requisicaoAtual === requisicaoRef.current) setLoading(false);
    }
  }, [filtrosRequisicao, podeLer]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  const membrosFiltrados = useMemo(() => filtrarMembros(dados.membros, {
    ...filtros,
    busca: buscaDebounced,
  }), [buscaDebounced, dados.membros, filtros]);
  const resumo = useMemo(() => calcularResumo(membrosFiltrados), [membrosFiltrados]);
  const regioes = useMemo(() => construirRegioes(membrosFiltrados), [membrosFiltrados]);
  const insights = useMemo(() => gerarInsights(regioes, resumo), [regioes, resumo]);
  const membrosOrdenados = useMemo(
    () => ordenarMembros(membrosFiltrados, ordenacao),
    [membrosFiltrados, ordenacao]
  );
  const membrosComCoords = useMemo(
    () => membrosFiltrados.filter(temCoordenadas),
    [membrosFiltrados]
  );
  const incluiJovens = filtros.categorias.includes('jovem');
  const mapaPronto = Boolean(isLoaded && window.google?.maps);
  const ultimaAtualizacao = useMemo(() => {
    const datas = dados.membros
      .map((membro) => membro.frequencia_atualizada_em)
      .filter(Boolean)
      .map((valor) => new Date(valor))
      .filter((data) => !Number.isNaN(data.getTime()));
    if (!datas.length) return null;
    return new Date(Math.max(...datas.map((data) => data.getTime())));
  }, [dados.membros]);

  const heatmaps = useMemo(() => membrosComCoords.reduce((acc, membro) => {
    const status = statusDoMembro(membro);
    const coords = coordenadasDoMembro(membro);
    if (!coords) return acc;
    const point = { lat: coords.lat, lng: coords.lng };
    if (status === STATUS.FREQUENTE || status === STATUS.COMPARECE) {
      acc.presenca.push({ ...point, weight: status === STATUS.FREQUENTE ? 2 : 1 });
    }
    if (status === STATUS.IRREGULAR || status === STATUS.NAO_COMPARECE) {
      acc.atencao.push({ ...point, weight: status === STATUS.NAO_COMPARECE ? 2 : 1 });
    }
    return acc;
  }, { presenca: [], atencao: [] }), [membrosComCoords]);

  const analiticos = useMemo(() => {
    const statusOrdem = [
      STATUS.FREQUENTE,
      STATUS.COMPARECE,
      STATUS.IRREGULAR,
      STATUS.NAO_COMPARECE,
      STATUS.NAO_INFORMADO,
      STATUS.DADO_INCONSISTENTE,
    ];
    const criancas = membrosFiltrados.filter((m) => m.categoria === 'crianca');
    const jovens = membrosFiltrados.filter((m) => m.categoria === 'jovem');
    const contagemStatus = (lista) => statusOrdem.map((s) => lista.filter((m) => statusDoMembro(m) === s).length);
    const topBairros = [...regioes]
      .sort((a, b) => b.quantidade_atencao - a.quantidade_atencao || b.total - a.total)
      .slice(0, 8);

    const chartStatus = {
      labels: statusOrdem.map((s) => getStatusConfig(s).label),
      datasets: [{
        data: statusOrdem.map((s) => resumo.por_status?.[s] ?? 0),
        backgroundColor: statusOrdem.map((s) => getStatusConfig(s).color),
        borderWidth: 0,
        hoverOffset: 4,
      }],
    };

    const chartGrupos = {
      labels: statusOrdem.map((s) => getStatusConfig(s).label),
      datasets: [
        {
          label: 'Crianças',
          data: contagemStatus(criancas),
          backgroundColor: '#3b82f6',
          borderRadius: 5,
        },
        {
          label: 'Jovens',
          data: contagemStatus(jovens),
          backgroundColor: '#d97706',
          borderRadius: 5,
        },
      ],
    };

    const chartBairros = {
      labels: topBairros.map((r) => r.bairro),
      datasets: [
        {
          label: 'Em atenção',
          data: topBairros.map((r) => r.quantidade_atencao),
          backgroundColor: '#dc2626',
          borderRadius: 5,
        },
        {
          label: 'Frequentes + comparecem',
          data: topBairros.map((r) => (r.frequentes || 0) + (r.comparecem || 0)),
          backgroundColor: '#15803d',
          borderRadius: 5,
        },
      ],
    };

    const chartBatismo = {
      labels: ['Batizados', 'Não batizados', 'Não informado'],
      datasets: [{
        data: [
          resumo.jovens_batizados || 0,
          resumo.jovens_nao_batizados || 0,
          resumo.jovens_batismo_nao_informado || 0,
        ],
        backgroundColor: ['#15803d', '#f59e0b', '#94a3b8'],
        borderWidth: 0,
      }],
    };

    const chartCobertura = {
      labels: ['Com GPS', 'Sem GPS'],
      datasets: [{
        data: [resumo.com_coordenadas || 0, resumo.sem_coordenadas || 0],
        backgroundColor: ['#0f766e', '#cbd5e1'],
        borderWidth: 0,
      }],
    };

    return {
      chartStatus,
      chartGrupos,
      chartBairros,
      chartBatismo,
      chartCobertura,
      topBairros,
      nCriancas: criancas.length,
      nJovens: jovens.length,
    };
  }, [membrosFiltrados, regioes, resumo]);

  const chartDoughnutOpts = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 10, font: { size: 10 }, padding: 10 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0) || 1;
            const pct = Math.round((ctx.raw / total) * 1000) / 10;
            return ` ${ctx.label}: ${ctx.raw} (${pct}%)`;
          },
        },
      },
    },
  }), []);

  const chartBarOpts = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 10, font: { size: 10 }, padding: 10 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${ctx.raw}`,
        },
      },
    },
    scales: {
      x: {
        ticks: { font: { size: 9 }, maxRotation: 35, minRotation: 0 },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0, font: { size: 10 } },
        grid: { color: 'rgba(148,163,184,.2)' },
      },
    },
  }), []);

  const criarIconeMarcador = useCallback((membro, selecionado) => {
    if (!window.google?.maps) return undefined;
    const config = getStatusConfig(statusDoMembro(membro));
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: selecionado ? 13 : 10,
      fillColor: config.color,
      fillOpacity: 0.95,
      strokeColor: selecionado ? '#111827' : '#ffffff',
      strokeWeight: selecionado ? 3 : 2,
    };
  }, []);

  const selecionarMembro = useCallback((membro) => {
    setMembroSelecionado(membro);
    const coords = coordenadasDoMembro(membro);
    if (coords && mapInstance) {
      mapInstance.panTo(coords);
      mapInstance.setZoom(Math.max(mapInstance.getZoom() || 14, 16));
    }
  }, [mapInstance]);

  const selecionarRegiao = useCallback((regiao) => {
    if (regiao.centroide && mapInstance) {
      mapInstance.panTo(regiao.centroide);
      mapInstance.setZoom(15);
    }
    setFiltros((current) => ({ ...current, bairro: regiao.bairro }));
    setAbaPainel('pessoas');
  }, [mapInstance]);

  const alternarCategoria = (tipo) => {
    const categorias = tipo === 'ambos' ? ['crianca', 'jovem'] : [tipo];
    setFiltros((current) => ({
      ...current,
      categorias,
      batizado: categorias.includes('jovem') ? current.batizado : '',
    }));
  };

  const alternarStatus = (status) => {
    setFiltros((current) => ({
      ...current,
      status: current.status.includes(status)
        ? current.status.filter((item) => item !== status)
        : [...current.status, status],
    }));
  };

  const limparFiltros = () => {
    setFiltros({ ...FILTROS_INICIAIS });
    setBuscaDebounced('');
    setMembroSelecionado(null);
  };

  const toggleTelaCheia = () => {
    const el = mapCardRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
      setTelaCheia(true);
    } else {
      document.exitFullscreen?.();
      setTelaCheia(false);
    }
  };

  useEffect(() => {
    const onFsChange = () => setTelaCheia(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const salvarFrequencia = async (form) => {
    if (!podeEditar || !editando) return;
    setSalvando(true);
    try {
      const payload = { ...form };
      if (editando.categoria !== 'jovem') delete payload.batizado;
      const atualizado = await analiseFrequenciaService.atualizar(editando.id, payload);
      setDados((current) => ({
        ...current,
        membros: current.membros.map((membro) => (
          membro.id === editando.id ? { ...membro, ...atualizado } : membro
        )),
      }));
      setMembroSelecionado((current) => (
        current?.id === editando.id ? { ...current, ...atualizado } : current
      ));
      setEditando(null);
      toast.success('Frequência atualizada com sucesso.');
      await carregarDados();
    } catch (error) {
      toast.error(error.message || 'Não foi possível atualizar a frequência.');
    } finally {
      setSalvando(false);
    }
  };

  const geocodificarPendentes = async () => {
    if (!podeEditar) return;
    setGeocodificando(true);
    try {
      const { data } = await api.post('/irmandade/geocodificar-pendentes');
      toast.success(data.total
        ? `Geocodificação iniciada para ${data.total} pessoa(s).`
        : 'Todos os cadastros já possuem localização.');
      if (data.total) setTimeout(carregarDados, 6000);
    } catch (error) {
      toast.error(error.message || 'Não foi possível iniciar a geocodificação.');
    } finally {
      setGeocodificando(false);
    }
  };

  const selecionarTodasAbas = () => setExportAbas(abasPadraoSelecionadas());
  const limparAbas = () => setExportAbas(Object.fromEntries(ABAS_EXPORTACAO_IDS.map((id) => [id, false])));
  const toggleAbaExport = (id) => {
    setExportAbas((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const confirmarExportacao = async () => {
    const abas = listaAbasSelecionadas(exportAbas);
    if (!abas.length) {
      toast.warn('Selecione ao menos uma seção para exportar.');
      return;
    }
    setExportando(true);
    try {
      const payload = { membros: membrosFiltrados, regioes, resumo, abas };
      if (exportFormato === 'pdf') {
        const result = await exportarAnaliseFrequenciaPdf(payload);
        toast.success(`PDF gerado! ${result.secoes} seção(ões) · ${result.pessoas} pessoas.`);
      } else {
        const result = await exportarAnaliseFrequenciaExcel(payload);
        toast.success(`Excel gerado! ${result.abas} aba(s) · ${result.pessoas} pessoas.`);
      }
      setExportAberto(false);
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Não foi possível gerar a exportação.');
    } finally {
      setExportando(false);
    }
  };

  const abrirWhatsApp = (membro) => {
    const telefone = (membro.responsavel_telefone || membro.telefone || '').replace(/\D/g, '');
    return telefone ? `https://wa.me/${telefone.startsWith('55') ? telefone : `55${telefone}`}` : null;
  };

  if (!podeLer) return null;

  const publicoAtivo = filtros.categorias.length === 2 ? 'ambos' : filtros.categorias[0];
  const temFiltros = Boolean(
    filtros.status.length || filtros.bairro || filtros.busca || filtros.tipo_pessoa ||
    filtros.localizacao !== 'todos' || filtros.batizado ||
    filtros.visita_indicada || filtros.aceitou_visita
  );
  const taxa = (value) => value == null ? '—' : `${value}%`;
  const googleIndisponivel = !googleApiKey || Boolean(loadError);

  return (
    <div className="aft-page">
      <header className="aft-header">
        <div className="aft-heading">
          <span className="aft-eyebrow"><FiMap /> Inteligência territorial</span>
          <h1>Análise territorial de frequência</h1>
          <p>Compare presença, necessidade de atenção e cobertura cadastral por bairro.</p>
          <span className="aft-updated">
            Última atualização de frequência: {formatarData(ultimaAtualizacao, true)}
          </span>
        </div>
        <div className="aft-header-actions">
          {podeEditar && resumo.sem_coordenadas > 0 && (
            <button type="button" className="aft-button aft-button-secondary" onClick={geocodificarPendentes} disabled={geocodificando}>
              <FiTarget /> {geocodificando ? 'Localizando…' : `Localizar pendentes (${resumo.sem_coordenadas})`}
            </button>
          )}
          <div className="aft-export" ref={exportRef}>
            <button
              type="button"
              className="aft-button aft-button-primary"
              onClick={() => setExportAberto((value) => !value)}
              aria-expanded={exportAberto}
            >
              <FiDownload /> Exportar <FiChevronDown />
            </button>
            {exportAberto && (
              <div className="aft-export-panel" role="dialog" aria-label="Opções de exportação">
                <div className="aft-export-panel-head">
                  <strong>Exportar análise</strong>
                  <button type="button" className="aft-export-close" onClick={() => setExportAberto(false)} aria-label="Fechar">
                    <FiX />
                  </button>
                </div>

                <p className="aft-export-hint">Escolha o formato e quais seções deseja incluir.</p>

                <div className="aft-export-format" role="radiogroup" aria-label="Formato">
                  <button
                    type="button"
                    className={exportFormato === 'excel' ? 'active' : ''}
                    onClick={() => setExportFormato('excel')}
                    aria-pressed={exportFormato === 'excel'}
                  >
                    <FiBarChart2 />
                    <span>Excel</span>
                  </button>
                  <button
                    type="button"
                    className={exportFormato === 'pdf' ? 'active' : ''}
                    onClick={() => setExportFormato('pdf')}
                    aria-pressed={exportFormato === 'pdf'}
                  >
                    <FiFileText />
                    <span>PDF</span>
                  </button>
                </div>

                <div className="aft-export-abas-toolbar">
                  <span>Seções</span>
                  <div>
                    <button type="button" onClick={selecionarTodasAbas}>Todas</button>
                    <button type="button" onClick={limparAbas}>Nenhuma</button>
                  </div>
                </div>

                <ul className="aft-export-abas">
                  {ABAS_EXPORTACAO.map((aba) => (
                    <li key={aba.id}>
                      <label>
                        <input
                          type="checkbox"
                          checked={Boolean(exportAbas[aba.id])}
                          onChange={() => toggleAbaExport(aba.id)}
                        />
                        <span>
                          <strong>{aba.label}</strong>
                          <small>{aba.descricao}</small>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  className="aft-button aft-button-primary aft-export-confirm"
                  onClick={confirmarExportacao}
                  disabled={exportando || !listaAbasSelecionadas(exportAbas).length}
                >
                  <FiDownload />
                  {exportando
                    ? 'Gerando…'
                    : `Exportar ${exportFormato === 'pdf' ? 'PDF' : 'Excel'} (${listaAbasSelecionadas(exportAbas).length})`}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <section className="aft-audience" aria-label="Público analisado">
        <div>
          <span className="aft-eyebrow">Público</span>
          <strong>Quem você quer analisar?</strong>
        </div>
        <div className="aft-segmented">
          {[
            ['crianca', 'Crianças'],
            ['jovem', 'Jovens'],
            ['ambos', 'Ambos'],
          ].map(([value, label]) => (
            <button
              type="button"
              key={value}
              className={publicoAtivo === value ? 'active' : ''}
              onClick={() => alternarCategoria(value)}
              aria-pressed={publicoAtivo === value}
            >
              {value === 'crianca' ? <FiUser /> : value === 'jovem' ? <FiUsers /> : <FiLayers />}
              {label}
            </button>
          ))}
        </div>
      </section>

      <section className={`aft-filters ${filtrosAbertos ? '' : 'collapsed'}`} aria-label="Filtros da análise">
        <div className="aft-filter-title">
          <span><FiFilter /> Filtros</span>
          <div className="aft-filter-actions">
            {temFiltros && <button type="button" onClick={limparFiltros}><FiX /> Limpar tudo</button>}
            <button type="button" onClick={() => setFiltrosAbertos((value) => !value)} aria-expanded={filtrosAbertos}>
              <FiChevronDown className={filtrosAbertos ? 'rotated' : ''} />
              {filtrosAbertos ? 'Recolher' : 'Mostrar'}
            </button>
          </div>
        </div>
        <div className="aft-status-filter">
          <span className="aft-field-label">Status (selecione vários)</span>
          <div className="aft-status-chips">
            {STATUS_OPTIONS.map((option) => (
              <button
                type="button"
                key={option.value}
                className={filtros.status.includes(option.value) ? 'active' : ''}
                onClick={() => alternarStatus(option.value)}
                aria-pressed={filtros.status.includes(option.value)}
                style={{ '--status-color': option.color, '--status-light': option.lightColor }}
              >
                <span>{option.shortLabel}</span>{option.label}
              </button>
            ))}
          </div>
        </div>
        <div className="aft-filter-grid">
          <label className="aft-field aft-search-field" htmlFor="aft-busca">
            <span>Busca</span>
            <div><FiSearch /><input id="aft-busca" value={filtros.busca} placeholder="Nome, endereço ou responsável" onChange={(event) => setFiltros((current) => ({ ...current, busca: event.target.value }))} /></div>
          </label>
          <label className="aft-field" htmlFor="aft-bairro">
            <span>Bairro</span>
            <select id="aft-bairro" value={filtros.bairro} onChange={(event) => setFiltros((current) => ({ ...current, bairro: event.target.value }))}>
              <option value="">Todos os bairros</option>
              {bairrosCatalogo.map((bairro) => <option key={bairro} value={bairro}>{bairro}</option>)}
            </select>
          </label>
          <label className="aft-field" htmlFor="aft-tipo">
            <span>Tipo</span>
            <select id="aft-tipo" value={filtros.tipo_pessoa} onChange={(event) => setFiltros((current) => ({ ...current, tipo_pessoa: event.target.value }))}>
              <option value="">Todos</option>
              <option value="irmao">Irmão</option>
              <option value="irma">Irmã</option>
            </select>
          </label>
          <label className="aft-field" htmlFor="aft-localizacao">
            <span>Localização</span>
            <select id="aft-localizacao" value={filtros.localizacao} onChange={(event) => setFiltros((current) => ({ ...current, localizacao: event.target.value }))}>
              <option value="todos">Todos</option>
              <option value="com">Com localização</option>
              <option value="sem">Sem localização</option>
            </select>
          </label>
          {incluiJovens && (
            <TriStateSelect id="aft-batizado" label="Batizado? (somente jovens)" value={filtros.batizado} onChange={(value) => setFiltros((current) => ({ ...current, batizado: value }))} />
          )}
          <TriStateSelect id="aft-visita" label="Visita indicada?" value={filtros.visita_indicada} onChange={(value) => setFiltros((current) => ({ ...current, visita_indicada: value }))} />
          <TriStateSelect id="aft-aceitou" label="Aceitou visita?" value={filtros.aceitou_visita} onChange={(value) => setFiltros((current) => ({ ...current, aceitou_visita: value }))} />
        </div>
      </section>

      {erro && (
        <div className="aft-feedback aft-feedback-error" role="alert">
          <FiAlertCircle />
          <span><strong>Falha ao carregar a análise</strong>{erro}</span>
          <button type="button" onClick={carregarDados}><FiRefreshCw /> Tentar novamente</button>
        </div>
      )}

      <button type="button" className="aft-indicators-toggle" onClick={() => setIndicadoresAbertos((value) => !value)} aria-expanded={indicadoresAbertos}>
        <FiBarChart2 /> Indicadores e insights
        <FiChevronDown className={indicadoresAbertos ? 'rotated' : ''} />
      </button>

      {indicadoresAbertos && <><section className="aft-kpi-grid" aria-label="Indicadores">
        <Indicador label="Total da amostra" value={resumo.total} detail={`${resumo.com_dados_conhecidos} com dados conhecidos`} />
        <Indicador label="Com localização" value={resumo.com_coordenadas} detail={`${taxa(resumo.cobertura_coordenadas)} de cobertura`} tone="info" />
        <Indicador label="Sem localização" value={resumo.sem_coordenadas} detail="Continuam na lista" tone={resumo.sem_coordenadas ? 'warning' : 'success'} />
        <Indicador label="Frequentes" value={resumo.frequentes} tone="success" />
        <Indicador label="Comparecem" value={resumo.comparecem} tone="success" />
        <Indicador label="Irregulares" value={resumo.irregulares} tone="warning" />
        <Indicador label="Não comparecem" value={resumo.nao_comparecem} tone="danger" />
        <Indicador label="Não informados" value={resumo.nao_informados} tone="muted" />
        <Indicador label="Dados inconsistentes" value={resumo.dados_inconsistentes} tone="purple" />
        <Indicador label="Taxa de comparecimento" value={taxa(resumo.taxa_comparecimento)} detail={`Amostra: ${resumo.com_dados_conhecidos}`} tone="success" title="Frequentes + comparecem, dividido somente pelos registros com status conhecido." />
        <Indicador label="Taxa de atenção" value={taxa(resumo.taxa_atencao)} detail={`${resumo.quantidade_atencao} pessoa(s)`} tone="warning" title="Irregulares + não comparecem, dividido somente pelos registros com status conhecido." />
        <Indicador label="Cobertura dos dados" value={taxa(resumo.cobertura_dados)} detail={`${resumo.com_dados_conhecidos} de ${resumo.total}`} tone="info" title="Percentual da amostra com status conhecido. Não informados e inconsistentes ficam fora das taxas." />
        {incluiJovens && <>
          <Indicador label="Jovens batizados" value={resumo.jovens_batizados} tone="success" />
          <Indicador label="Jovens não batizados" value={resumo.jovens_nao_batizados} tone="warning" />
          <Indicador label="Batismo não informado" value={resumo.jovens_batismo_nao_informado} tone="muted" />
        </>}
      </section>

      {resumo.sem_coordenadas > 0 && (
        <div className="aft-feedback aft-feedback-warning" role="status">
          <FiMapPin />
          <span>
            <strong>{resumo.sem_coordenadas} pessoa(s) não puderam ser posicionadas no mapa.</strong>
            Elas continuam nos indicadores, na lista e no agrupamento por bairro quando o endereço permite.
          </span>
        </div>
      )}

      <section className="aft-insights" aria-label="Insights territoriais">
        <article>
          <span className="aft-insight-icon aft-insight-danger"><FiTarget /></span>
          <div>
            <small>Menor taxa de comparecimento</small>
            {insights.menor_taxa
              ? <><strong>{insights.menor_taxa.bairro} · {taxa(insights.menor_taxa.taxa_comparecimento)}</strong><p>Amostra de {insights.menor_taxa.conhecidos} pessoas com dados conhecidos.</p></>
              : <><strong>Dados insuficientes</strong><p>São necessárias ao menos {insights.minimo_conhecidos_por_bairro} pessoas com dados por bairro.</p></>}
          </div>
        </article>
        <article>
          <span className="aft-insight-icon aft-insight-warning"><FiUsers /></span>
          <div>
            <small>Maior quantidade em atenção</small>
            {insights.maior_atencao
              ? <><strong>{insights.maior_atencao.bairro} · {insights.maior_atencao.pessoas} pessoa(s)</strong><p>{taxa(insights.maior_atencao.taxa_atencao)} de {insights.maior_atencao.conhecidos} com dados conhecidos.</p></>
              : <><strong>Dados insuficientes</strong><p>Nenhum bairro atingiu a amostra mínima.</p></>}
          </div>
        </article>
        <article>
          <span className="aft-insight-icon aft-insight-info"><FiBarChart2 /></span>
          <div>
            <small>Cobertura da amostra</small>
            <strong>{taxa(insights.cobertura.dados_percentual)} dados · {taxa(insights.cobertura.coordenadas_percentual)} mapa</strong>
            <p>{insights.cobertura.conhecidos} de {insights.cobertura.total} pessoas possuem frequência conhecida.</p>
          </div>
        </article>
      </section>

      {resumo.total > 0 && (
        <section className="aft-charts" aria-label="Gráficos analíticos">
          <header className="aft-charts-head">
            <FiBarChart2 />
            <div>
              <strong>Painel analítico</strong>
              <small>
                {analiticos.nCriancas} criança(s) · {analiticos.nJovens} jovem(ns) · filtros atuais
              </small>
            </div>
          </header>

          <div className="aft-charts-grid">
            <article className="aft-chart-card">
              <h3>Distribuição por status</h3>
              <p>Como está a frequência da amostra filtrada.</p>
              <div className="aft-chart-wrap aft-chart-wrap-doughnut">
                <Doughnut data={analiticos.chartStatus} options={chartDoughnutOpts} />
              </div>
            </article>

            <article className="aft-chart-card">
              <h3>Crianças × Jovens</h3>
              <p>Comparativo de status entre os dois grupos.</p>
              <div className="aft-chart-wrap">
                <Bar data={analiticos.chartGrupos} options={chartBarOpts} />
              </div>
            </article>

            <article className="aft-chart-card aft-chart-card-wide">
              <h3>Top bairros · atenção × presença</h3>
              <p>Onde há mais necessidade de visita e onde a presença é mais forte.</p>
              <div className="aft-chart-wrap aft-chart-wrap-wide">
                {analiticos.topBairros.length
                  ? <Bar data={analiticos.chartBairros} options={chartBarOpts} />
                  : <span className="aft-chart-empty">Sem bairros suficientes para o gráfico.</span>}
              </div>
            </article>

            {incluiJovens && (
              <article className="aft-chart-card">
                <h3>Batismo dos jovens</h3>
                <p>Situação cadastral de batismo.</p>
                <div className="aft-chart-wrap aft-chart-wrap-doughnut">
                  <Doughnut data={analiticos.chartBatismo} options={chartDoughnutOpts} />
                </div>
              </article>
            )}

            <article className="aft-chart-card">
              <h3>Cobertura no mapa</h3>
              <p>Quem tem GPS para aparecer no mapa.</p>
              <div className="aft-chart-wrap aft-chart-wrap-doughnut">
                <Doughnut data={analiticos.chartCobertura} options={chartDoughnutOpts} />
              </div>
            </article>
          </div>
        </section>
      )}
      </>}

      <section className={`aft-workspace ${painelAberto ? '' : 'panel-collapsed'}`}>
        <div className="aft-map-card" ref={mapCardRef}>
          <div className="aft-map-toolbar">
            <div className="aft-map-modes" aria-label="Modo do mapa">
              {[
                ['pontos', 'Pontos', FiMapPin],
                ['presenca', 'Presença', FiUsers],
                ['atencao', 'Atenção', FiTarget],
                ['comparativo', 'Comparativo', FiLayers],
              ].map(([value, label, Icon]) => (
                <button
                  type="button"
                  key={value}
                  className={modoMapa === value ? 'active' : ''}
                  onClick={() => setModoMapa(value)}
                  disabled={value !== 'pontos' && !mapaPronto}
                  title={value !== 'pontos' && !mapaPronto ? 'Aguarde o mapa carregar' : undefined}
                >
                  <Icon /> {label}
                </button>
              ))}
            </div>
            <span className="aft-map-count">{membrosComCoords.length} no mapa · {resumo.sem_coordenadas} sem local</span>
            <button type="button" className="aft-fullscreen-btn" onClick={toggleTelaCheia} title={telaCheia ? 'Sair da tela cheia' : 'Tela cheia'}>
              {telaCheia ? <FiMinimize2 /> : <FiMaximize2 />}
            </button>
          </div>

          {modoMapa === 'comparativo' && mapaPronto && (
            <div className="aft-layer-controls">
              <label><input type="checkbox" checked={comparativo.presenca} onChange={(event) => setComparativo((current) => ({ ...current, presenca: event.target.checked }))} /> Presença</label>
              <input aria-label="Opacidade da presença" type="range" min=".2" max="1" step=".05" value={comparativo.opacidadePresenca} onChange={(event) => setComparativo((current) => ({ ...current, opacidadePresenca: Number(event.target.value) }))} />
              <label><input type="checkbox" checked={comparativo.atencao} onChange={(event) => setComparativo((current) => ({ ...current, atencao: event.target.checked }))} /> Atenção</label>
              <input aria-label="Opacidade da atenção" type="range" min=".2" max="1" step=".05" value={comparativo.opacidadeAtencao} onChange={(event) => setComparativo((current) => ({ ...current, opacidadeAtencao: Number(event.target.value) }))} />
            </div>
          )}

          <div className="aft-map-wrap">
            {googleIndisponivel ? (
              <div className="aft-map-unavailable">
                <FiMap size={34} />
                <strong>Google Maps indisponível</strong>
                <p>{!googleApiKey ? 'A chave do Google Maps não está configurada.' : 'O mapa não pôde ser carregado. Os indicadores e as listas continuam disponíveis.'}</p>
              </div>
            ) : !isLoaded ? (
              <div className="aft-map-unavailable"><span className="aft-spinner" /><strong>Carregando Google Maps…</strong></div>
            ) : (
              <GoogleMap
                mapContainerClassName="aft-google-map"
                center={CENTRO_AREIOPOLIS}
                zoom={14}
                options={MAP_OPTIONS}
                onLoad={setMapInstance}
                onUnmount={() => setMapInstance(null)}
              >
                {modoMapa === 'pontos' && membrosComCoords.map((membro) => {
                  const config = getStatusConfig(statusDoMembro(membro));
                  return (
                    <MarkerF
                      key={membro.id}
                      position={coordenadasDoMembro(membro)}
                      icon={criarIconeMarcador(membro, membroSelecionado?.id === membro.id)}
                      label={{
                        text: config.shortLabel,
                        color: '#fff',
                        fontSize: '10px',
                        fontWeight: '700',
                      }}
                      title={`${membro.nome}: ${config.label}`}
                      aria-label={`${membro.nome}: ${config.label}`}
                      zIndex={membroSelecionado?.id === membro.id ? 20 : 1}
                      onClick={() => setMembroSelecionado(membro)}
                    />
                  );
                })}
                {modoMapa === 'presenca' && (
                  <CanvasHeatmapOverlay points={heatmaps.presenca} radius={38} opacity={0.75} gradient={PRESENCA_GRADIENT} />
                )}
                {modoMapa === 'atencao' && (
                  <CanvasHeatmapOverlay points={heatmaps.atencao} radius={40} opacity={0.8} gradient={ATENCAO_GRADIENT} />
                )}
                {modoMapa === 'comparativo' && comparativo.presenca && (
                  <CanvasHeatmapOverlay points={heatmaps.presenca} radius={36} opacity={comparativo.opacidadePresenca} gradient={PRESENCA_GRADIENT} />
                )}
                {modoMapa === 'comparativo' && comparativo.atencao && (
                  <CanvasHeatmapOverlay points={heatmaps.atencao} radius={42} opacity={comparativo.opacidadeAtencao} gradient={ATENCAO_GRADIENT} />
                )}
              </GoogleMap>
            )}

            {membroSelecionado && (
              <article className="aft-popup" aria-label={`Detalhes de ${membroSelecionado.nome}`}>
                <button type="button" className="aft-popup-close" onClick={() => setMembroSelecionado(null)} aria-label="Fechar detalhes"><FiX /></button>
                <span className="aft-status-badge" style={{ '--badge-color': getStatusConfig(statusDoMembro(membroSelecionado)).color, '--badge-bg': getStatusConfig(statusDoMembro(membroSelecionado)).lightColor }}>
                  {getStatusConfig(statusDoMembro(membroSelecionado)).shortLabel} · {getStatusConfig(statusDoMembro(membroSelecionado)).label}
                </span>
                <h3>{membroSelecionado.nome}</h3>
                <p>{membroSelecionado.categoria === 'crianca' ? 'Criança' : 'Jovem'}{membroSelecionado.idade != null ? ` · ${membroSelecionado.idade} anos` : ''}</p>
                <dl>
                  <div><dt>Bairro</dt><dd>{membroSelecionado.bairro || 'sem endereço'}</dd></div>
                  <div><dt>É frequente?</dt><dd>{formatarTriState(membroSelecionado.frequente_reuniao)}</dd></div>
                  <div><dt>Comparece?</dt><dd>{formatarTriState(membroSelecionado.comparece_reuniao)}</dd></div>
                  <div><dt>Visita indicada?</dt><dd>{formatarTriState(membroSelecionado.visita_indicada)}</dd></div>
                  {membroSelecionado.categoria === 'jovem' && (
                    <div><dt>Batizado?</dt><dd>{formatarTriState(membroSelecionado.batizado)}</dd></div>
                  )}
                  <div><dt>Aceitou visita?</dt><dd>{formatarTriState(membroSelecionado.aceitou_visita)}</dd></div>
                  <div><dt>Última atualização</dt><dd>{formatarData(membroSelecionado.frequencia_atualizada_em, true)}</dd></div>
                </dl>
                {membroSelecionado.endereco && <p className="aft-popup-address"><FiMapPin /> {membroSelecionado.endereco}</p>}
                {membroSelecionado.responsavel && <p className="aft-popup-resp">Responsável: {membroSelecionado.responsavel}</p>}
                <div className="aft-popup-actions">
                  {podeEditar && <button type="button" onClick={() => navigate(`/irmandade/editar/${membroSelecionado.id}`)}><FiExternalLink /> Cadastro</button>}
                  {temCoordenadas(membroSelecionado) && <a href={`https://www.google.com/maps?q=${coordenadasDoMembro(membroSelecionado).lat},${coordenadasDoMembro(membroSelecionado).lng}`} target="_blank" rel="noreferrer"><FiMapPin /> Maps</a>}
                  {abrirWhatsApp(membroSelecionado) && <a href={abrirWhatsApp(membroSelecionado)} target="_blank" rel="noreferrer"><FiMessageCircle /> WhatsApp</a>}
                  {podeEditar && <button type="button" onClick={() => setEditando(membroSelecionado)}><FiEdit3 /> Frequência</button>}
                </div>
              </article>
            )}

            <div className="aft-legend">
              {modoMapa === 'pontos' ? STATUS_OPTIONS.map((option) => (
                <span key={option.value}><i style={{ '--legend-color': option.color }}>{option.shortLabel}</i>{option.label}</span>
              )) : modoMapa === 'comparativo' ? (
                <><span><i className="aft-gradient-presenca" />Presença</span><span><i className="aft-gradient-atencao" />Atenção</span></>
              ) : (
                <span><i className={modoMapa === 'presenca' ? 'aft-gradient-presenca' : 'aft-gradient-atencao'} />Peso 1 → 2</span>
              )}
            </div>
          </div>
        </div>

        <aside className={`aft-side-panel ${painelAberto ? '' : 'collapsed'}`}>
          <button type="button" className="aft-panel-toggle" onClick={() => setPainelAberto((value) => !value)} aria-label={painelAberto ? 'Recolher painel' : 'Abrir painel'}>
            {painelAberto ? <FiChevronRight /> : <FiChevronLeft />}
          </button>
          {painelAberto && <>
            <div className="aft-panel-tabs">
              <button type="button" className={abaPainel === 'pessoas' ? 'active' : ''} onClick={() => setAbaPainel('pessoas')}><FiList /> Pessoas <span>{membrosFiltrados.length}</span></button>
              <button type="button" className={abaPainel === 'regioes' ? 'active' : ''} onClick={() => setAbaPainel('regioes')}><FiMap /> Regiões <span>{regioes.length}</span></button>
            </div>
            {abaPainel === 'pessoas' ? (
              <>
                <label className="aft-sort" htmlFor="aft-ordenacao"><span>Ordenar por</span><select id="aft-ordenacao" value={ordenacao} onChange={(event) => setOrdenacao(event.target.value)}><option value="status">Status</option><option value="nome">Nome</option><option value="bairro">Bairro</option><option value="idade">Idade</option></select></label>
                <div className="aft-panel-list">
                  {membrosOrdenados.map((membro) => {
                    const config = getStatusConfig(statusDoMembro(membro));
                    return (
                      <button type="button" className={`aft-person-row ${membroSelecionado?.id === membro.id ? 'active' : ''}`} key={membro.id} onClick={() => selecionarMembro(membro)}>
                        <span className="aft-row-status" style={{ '--row-color': config.color }}>{config.shortLabel}</span>
                        <span className="aft-row-main">
                          <strong>{membro.nome}</strong>
                          <small>{membro.categoria === 'crianca' ? 'Criança' : 'Jovem'} · {membro.bairro || 'sem endereço'} · {membro.idade ?? '—'} anos</small>
                          {membro.endereco && <small title={membro.endereco}>{membro.endereco}</small>}
                          {membro.responsavel && <small>Responsável: {membro.responsavel}</small>}
                          {membro.categoria === 'jovem' && <small>Batizado: {formatarTriState(membro.batizado)}</small>}
                          <em>{config.label}</em>
                        </span>
                        {!temCoordenadas(membro) && <FiMapPin className="aft-no-location" aria-label="Sem localização" />}
                        {podeEditar && <span className="aft-row-edit" role="button" tabIndex="0" title="Editar frequência" onClick={(event) => { event.stopPropagation(); setEditando(membro); }} onKeyDown={(event) => { if (event.key === 'Enter') { event.stopPropagation(); setEditando(membro); } }}><FiEdit3 /></span>}
                      </button>
                    );
                  })}
                  {!loading && membrosOrdenados.length === 0 && <div className="aft-empty"><FiSearch /><strong>Nenhum resultado</strong><p>Ajuste ou limpe os filtros para ampliar a amostra.</p>{temFiltros && <button type="button" onClick={limparFiltros}>Limpar filtros</button>}</div>}
                </div>
              </>
            ) : (
              <div className="aft-panel-list aft-region-list">
                {regioes.map((regiao, index) => (
                  <button type="button" key={regiao.bairro} onClick={() => selecionarRegiao(regiao)}>
                    <span className="aft-rank">{index + 1}</span>
                    <span>
                      <strong>{regiao.bairro}</strong>
                      <small>{regiao.total} pessoa(s) · dados conhecidos {regiao.com_dados_conhecidos}</small>
                      <small>{regiao.presenca} comparecem · {regiao.irregulares} irregulares · {regiao.nao_comparecem} não comparecem</small>
                      <small>{regiao.nao_informados} não informados · taxa {taxa(regiao.taxa_comparecimento)}</small>
                    </span>
                    <span className="aft-region-rate"><strong>{regiao.quantidade_atencao}</strong><small>atenção</small></span>
                  </button>
                ))}
                {!regioes.length && <div className="aft-empty"><FiMap /><strong>Sem regiões na amostra</strong></div>}
              </div>
            )}
          </>}
        </aside>
      </section>

      {loading && (
        <div className="aft-loading-overlay" aria-live="polite">
          <span className="aft-spinner" /> Atualizando análise…
        </div>
      )}
      {editando && podeEditar && (
        <ModalEdicao membro={editando} salvando={salvando} onClose={() => setEditando(null)} onSave={salvarFrequencia} />
      )}
    </div>
  );
};

export default AnaliseFrequenciaTerritorial;
